import React from 'react';
import { Paper, Typography, Box, useTheme } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import ShareButton from './ShareButton';

interface MessageBubbleProps {
  content: string;
  type: 'ai' | 'user';
  createdAt: string;
  isLatest?: boolean;
  image?: string;
  onShare?: () => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ content, type, createdAt, isLatest, image, onShare }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  return (
    <Paper
      elevation={isLatest ? 4 : 2}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '2px solid',
        borderColor: isLatest
          ? (isDark ? 'primary.light' : 'primary.main')
          : (isDark ? 'rgba(255,255,255,0.1)' : 'grey.300'),
        bgcolor: isDark ? 'rgba(0,0,0,0.18)' : 'background.paper',
        color: isDark ? 'common.white' : 'text.primary',
        maxWidth: 700,
        width: '100%',
        mb: 1,
        boxShadow: isLatest ? 4 : 1,
        position: 'relative',
      }}
    >
      {/* Share button in top right */}
      {onShare && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
          <ShareButton onClick={onShare} ariaLabel="Share this message" inline={true} />
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" color={type === 'ai' ? 'primary.main' : 'secondary.main'} sx={{ fontWeight: 600, fontSize: 14 }}>
          {type === 'ai' ? 'AI' : 'User'}
        </Typography>
        <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary', fontWeight: 400 }}>
          {new Date(createdAt).toLocaleString()}
        </Typography>
        {isLatest && (
          <Typography variant="caption" sx={{ ml: 2, color: 'success.main', fontWeight: 700 }}>
            Latest
          </Typography>
        )}
      </Box>
      {image && (
        <Box sx={{ mb: 2 }}>
          <img 
            src={image} 
            alt="Analysis image"
            style={{ 
              maxWidth: '100%', 
              maxHeight: 300, 
              borderRadius: 8,
              border: '1px solid rgba(0,0,0,0.1)'
            }} 
          />
        </Box>
      )}
      <Box sx={{ fontSize: 15, lineHeight: 1.7 }}>
        <ReactMarkdown>{content}</ReactMarkdown>
      </Box>
    </Paper>
  );
};

export default MessageBubble; 